// Góc nhìn Tư vấn cơ sở (sale tại cơ sở): khách đến theo lịch Telesales đặt → check-in → tư vấn → kết quả
import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Form, Input, InputNumber, Modal, Radio, Segmented, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { CalendarClock, CalendarDays, CheckCircle2, ChevronDown, Hourglass, List, MessagesSquare, UserCheck, UserX, Wallet } from 'lucide-react';
import ApptCalendar from './ApptCalendar';
import { BRANCHES, CONSULTANTS, DAY, MIN, type Appt, type ApptStatus } from './data';
import {
  apptAmount, apptConsultant, apptStatus, apptsOn, branchKpi, consultantPerf, isLate, leadStage, pct, rangeOf, returnedToTele, type ConsultPerf, type Ctx,
} from './logic';
import useTeleDash from './store';
import { APPT_STATUS, AgentName, Card, ConsultantName, Empty, Kpi, StageChip, dm, dur, hm, money } from './ui';

type Group = 'all' | 'todo' | 'here' | 'done';
const GROUP_OF: Record<ApptStatus, Group> = {
  booked: 'todo', confirmed: 'todo', arrived: 'here', consulting: 'here', won: 'done', deposit: 'done', no_need: 'done', medical: 'done', noshow: 'done', cancelled: 'done',
};
type Outcome = 'won' | 'deposit' | 'no_need' | 'medical';

export default function BranchView({ ctx }: { ctx: Ctx }) {
  const { branchId, consultantId, set, patchAppt } = useTeleDash();
  const [day, setDay] = useState<'today' | 'tomorrow'>('today');
  const [display, setDisplay] = useState<'list' | 'calendar'>('list');
  const [group, setGroup] = useState<Group>('all');
  const [perfPeriod, setPerfPeriod] = useState<'today' | 'month'>('today');
  const [result, setResult] = useState<Appt | null>(null);
  const [form] = Form.useForm<{ outcome: Outcome; amount?: number; reason?: string; consultantId?: string }>();
  const outcome = Form.useWatch('outcome', form);
  const { now, m } = ctx;
  const today = dayjs(now).startOf('day').valueOf();
  const dayStart = day === 'today' ? today : today + DAY;
  const branchCons = CONSULTANTS.filter(c => c.branchId === branchId);

  const list = useMemo(() => apptsOn(ctx, dayStart, branchId).filter(a => !consultantId || [undefined, consultantId].includes(apptConsultant(a, ctx))), [ctx, dayStart, branchId, consultantId]);
  const k = branchKpi(list, ctx);
  const calendarAppts = useMemo(() => m.appts.filter(a => a.branchId === branchId && (!consultantId || [undefined, consultantId].includes(apptConsultant(a, ctx)))), [m, ctx, branchId, consultantId]);
  const shown = list.filter(a => group === 'all' || GROUP_OF[apptStatus(a, ctx)] === group);
  const perf = useMemo(() => consultantPerf(ctx, rangeOf(perfPeriod, now), branchId), [ctx, perfPeriod, now, branchId]);
  const back = useMemo(() => returnedToTele(ctx, branchId), [ctx, branchId]);
  const longestWait = list.filter(a => apptStatus(a, ctx) === 'arrived').map(a => now - (ctx.ov.appt[a.id]?.updatedAt ?? a.at)).sort((a, b) => b - a)[0];

  const act = (a: Appt, status: ApptStatus, extra: Partial<{ consultantId: string; amount: number; reason: string }> = {}, text?: string) => {
    patchAppt(a.id, { status, ...extra });
    message.success(text ?? `${m.leadById[a.leadId].name}: ${APPT_STATUS[status].label}`);
  };
  const start = (a: Appt, cid: string) => act(a, 'consulting', { consultantId: cid }, `Bắt đầu tư vấn ${m.leadById[a.leadId].name} · ${CONSULTANTS.find(c => c.id === cid)?.name}`);
  const openResult = (a: Appt) => {
    const p = ctx.ov.appt[a.id];
    const st = apptStatus(a, ctx);
    form.setFieldsValue({ outcome: (['won', 'deposit', 'no_need', 'medical'].includes(st) ? st : 'won') as Outcome, amount: p?.amount ?? (apptAmount(a, ctx) || undefined), reason: p?.reason, consultantId: apptConsultant(a, ctx) ?? consultantId ?? branchCons[0]?.id });
    setResult(a);
  };
  const saveResult = async () => {
    if (!result) return;
    const v = await form.validateFields();
    act(result, v.outcome, { consultantId: v.consultantId, amount: v.amount ?? 0, reason: v.reason });
    setResult(null);
  };

  const actions = (a: Appt) => {
    const st = apptStatus(a, ctx);
    // Lịch các ngày sau: cơ sở chỉ xem, Telesales lo xác nhận
    if (a.at >= today + DAY) return st === 'booked' ? <span className="text-[11px] text-gray-400">Telesales đang xác nhận</span> : null;
    if (st === 'booked' || st === 'confirmed') {
      return (
        <div className="flex justify-end gap-1">
          <Button size="small" type="primary" icon={<UserCheck size={13} />} onClick={() => act(a, 'arrived', {}, `Đã check-in ${m.leadById[a.leadId].name}`)}>Check-in</Button>
          <Tooltip title={a.at > now ? 'Chưa tới giờ hẹn' : 'Khách không đến — trả về Telesales gọi lại'}>
            <Button size="small" danger disabled={a.at > now} icon={<UserX size={13} />} onClick={() => act(a, 'noshow')} />
          </Tooltip>
        </div>
      );
    }
    if (st === 'arrived') {
      const pickCons = consultantId
        ? <Button size="small" type="primary" onClick={() => start(a, consultantId)}>Bắt đầu tư vấn</Button>
        : (
          <Dropdown trigger={['click']} menu={{ items: branchCons.map(c => ({ key: c.id, label: <ConsultantName id={c.id} /> })), onClick: e => start(a, e.key) }}>
            <Button size="small" type="primary">Bắt đầu tư vấn <ChevronDown size={12} /></Button>
          </Dropdown>
        );
      return <div className="flex justify-end">{pickCons}</div>;
    }
    if (st === 'consulting') return <div className="flex justify-end"><Button size="small" type="primary" onClick={() => openResult(a)}>Ghi kết quả</Button></div>;
    if (['won', 'deposit', 'no_need', 'medical'].includes(st)) return <div className="flex justify-end"><Button size="small" type="link" onClick={() => openResult(a)}>Sửa kết quả</Button></div>;
    if (st === 'noshow') return <div className="flex justify-end"><Button size="small" type="link" onClick={() => act(a, 'arrived', {}, 'Khách đến muộn — đã check-in')}>Khách đến muộn</Button></div>;
    return null;
  };

  const cols: ColumnsType<Appt> = [
    { title: 'Giờ hẹn', key: 'at', width: 86, render: (_, a) => <b className="text-[14px] text-gray-800 dark:text-gray-100">{hm(a.at)}</b> },
    {
      title: 'Khách', key: 'l', render: (_, a) => {
        const l = m.leadById[a.leadId];
        return (
          <div className="leading-tight">
            <div className="flex items-center gap-1.5 whitespace-nowrap"><b className="text-gray-800 dark:text-gray-100">{l.name}</b><StageChip s={leadStage(l, ctx)} /></div>
            <div className="text-[11px] text-gray-500">{l.id} · {l.phone}</div>
          </div>
        );
      },
    },
    {
      title: 'Nhu cầu (Telesales ghi)', key: 'note', render: (_, a) => (
        <div className="max-w-[300px] leading-tight">
          <div className="text-[12px] font-medium text-gray-700 dark:text-gray-200">{a.kind} · {m.leadById[a.leadId].mang}</div>
          <div className="text-[11px] text-gray-500">{a.note}</div>
        </div>
      ),
    },
    { title: 'Telesales đặt', key: 'by', render: (_, a) => <AgentName id={a.bookedBy} sub={`Đặt ${dm(a.bookedAt)} ${hm(a.bookedAt)}`} /> },
    { title: 'Tư vấn viên', key: 'tv', render: (_, a) => <ConsultantName id={apptConsultant(a, ctx)} /> },
    {
      title: 'Trạng thái', key: 'st', render: (_, a) => {
        const st = apptStatus(a, ctx);
        const p = ctx.ov.appt[a.id];
        return (
          <div className="leading-tight">
            <Tag color={APPT_STATUS[st].color} className="!m-0">{APPT_STATUS[st].label}</Tag>
            {isLate(a, ctx) && <div className="mt-0.5 text-[11px] font-semibold text-red-500">Trễ {dur(now - a.at)}</div>}
            {st === 'arrived' && p?.updatedAt && <div className={`mt-0.5 text-[11px] ${now - p.updatedAt > 15 * MIN ? 'font-semibold text-amber-600' : 'text-gray-500'}`}>Chờ {dur(now - p.updatedAt)}</div>}
            {(st === 'won' || st === 'deposit') && <div className="mt-0.5 text-[11px] font-semibold text-green-600">{money(apptAmount(a, ctx))}</div>}
            {p?.reason && <div className="mt-0.5 max-w-[180px] truncate text-[11px] text-gray-500" title={p.reason}>{p.reason}</div>}
          </div>
        );
      },
    },
    { title: '', key: 'act', width: 170, align: 'right', render: (_, a) => actions(a) },
  ];

  const perfCols: ColumnsType<ConsultPerf> = [
    { title: 'Tư vấn viên', key: 'n', render: (_, p) => <ConsultantName id={p.id} /> },
    { title: 'Đã tư vấn', dataIndex: 'served', align: 'right' },
    { title: 'Chốt / Cọc', key: 'c', align: 'right', render: (_, p) => <span>{p.closed} <span className="text-gray-400">({pct(p.closed, p.served)}%)</span></span> },
    { title: 'Doanh số', key: 'r', align: 'right', render: (_, p) => <b>{money(p.revenue + p.deposit)}</b> },
  ];

  const counts: Record<Group, number> = { all: list.length, todo: 0, here: 0, done: 0 };
  list.forEach(a => { counts[GROUP_OF[apptStatus(a, ctx)]]++; });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <Select className="w-56" value={branchId} onChange={v => set({ branchId: v, consultantId: undefined })} options={BRANCHES.map(b => ({ value: b.id, label: b.name }))} />
        <Select className="w-56" allowClear placeholder="Tất cả tư vấn viên" value={consultantId} onChange={v => set({ consultantId: v })}
          options={branchCons.map(c => ({ value: c.id, label: <ConsultantName id={c.id} /> }))} />
        <Segmented value={day} onChange={v => setDay(v as typeof day)} options={[{ value: 'today', label: `Hôm nay ${dm(today)}` }, { value: 'tomorrow', label: `Ngày mai ${dm(today + DAY)}` }]} />
        <span className="ml-auto text-xs text-gray-500">Cập nhật {hm(now)}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Kpi icon={<CalendarClock size={16} />} tone="teal" label="Lịch hẹn" value={k.total} sub={day === 'today' ? `${counts.todo} lịch chưa đến` : 'Do Telesales đặt'} onClick={() => setGroup('all')} active={group === 'all'} />
        <Kpi icon={<UserCheck size={16} />} tone="sky" label="Đã đến" value={k.arrived} sub={`${pct(k.arrived, k.total)}% số lịch`} />
        <Kpi icon={<Hourglass size={16} />} tone={k.waiting ? 'amber' : 'slate'} label="Đang chờ / đang tư vấn" value={`${k.waiting} / ${k.consulting}`}
          sub={longestWait ? <>Chờ lâu nhất <b className={longestWait > 15 * MIN ? 'text-amber-600' : ''}>{dur(longestWait)}</b></> : 'Không có khách chờ'} onClick={() => setGroup('here')} active={group === 'here'} />
        <Kpi icon={<CheckCircle2 size={16} />} tone="green" label="Chốt / Cọc" value={k.closed} sub={`Tỷ lệ chốt ${pct(k.closed, k.resolved)}% khách đã tư vấn`} />
        <Kpi icon={<Wallet size={16} />} tone="green" label="Doanh số" value={money(k.revenue)} sub={`+ ${money(k.deposit)} tiền cọc`} />
        <Kpi icon={<UserX size={16} />} tone="red" label="Quá giờ / không đến" value={`${k.late} / ${k.noshow}`} alert={k.late > 0} sub="Không đến → trả về Telesales" onClick={() => setGroup('todo')} active={group === 'todo'} />
      </div>

      <div className="grid gap-4 2xl:grid-cols-[1fr_380px]">
        <Card title={`Lịch khách đến — ${BRANCHES.find(b => b.id === branchId)?.name}`}
          extra={
            <div className="flex flex-wrap items-center gap-2">
              {display === 'list' && <Segmented size="small" value={group} onChange={v => setGroup(v as Group)} options={[
                { value: 'all', label: `Tất cả (${counts.all})` }, { value: 'todo', label: `Chưa đến (${counts.todo})` }, { value: 'here', label: `Tại cơ sở (${counts.here})` }, { value: 'done', label: `Đã xong (${counts.done})` },
              ]} />}
              <Segmented size="small" value={display} onChange={v => setDisplay(v as typeof display)} options={[
                { value: 'list', label: <span className="inline-flex items-center gap-1"><List size={13} />Danh sách</span> },
                { value: 'calendar', label: <span className="inline-flex items-center gap-1"><CalendarDays size={13} />Lịch</span> },
              ]} />
            </div>
          }>
          {display === 'calendar'
            ? <ApptCalendar ctx={ctx} appts={calendarAppts} consultants={consultantId ? branchCons.filter(c => c.id === consultantId) : branchCons} actions={actions} />
            : <Table<Appt> size="small" rowKey="id" columns={cols} dataSource={shown} pagination={false} scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'Không có lịch hẹn' }}
              rowClassName={a => (isLate(a, ctx) ? 'bg-red-50/60 dark:bg-red-500/5' : apptStatus(a, ctx) === 'arrived' ? 'bg-amber-50/60 dark:bg-amber-500/5' : '')} />}
          <p className="mt-2 text-xs text-gray-500">Luồng: Check-in khi khách tới quầy → Bắt đầu tư vấn (chọn tư vấn viên) → Ghi kết quả. Kết quả cập nhật ngay về Telesales và màn quản lý.</p>
        </Card>
        <div className="flex min-w-0 flex-col gap-4">
          <Card title="Hiệu suất tư vấn viên" extra={<Segmented size="small" value={perfPeriod} onChange={v => setPerfPeriod(v as typeof perfPeriod)} options={[{ value: 'today', label: 'Hôm nay' }, { value: 'month', label: 'Tháng' }]} />}>
            <Table<ConsultPerf> size="small" rowKey="id" columns={perfCols} dataSource={perf} pagination={false} />
          </Card>
          <Card title={<span className="inline-flex items-center gap-2"><MessagesSquare size={16} className="text-[#3e91ab]" />Trả về Telesales chăm tiếp</span>} extra={<span className="text-xs text-gray-500">7 ngày</span>}>
            {back.length ? (
              <div className="max-h-[360px] space-y-2 overflow-auto pr-1">
                {back.map(a => {
                  const l = m.leadById[a.leadId];
                  const st = apptStatus(a, ctx);
                  return (
                    <div key={a.id} className="flex items-start justify-between gap-2 rounded-lg border border-gray-100 p-2 text-[12px] dark:border-gray-700">
                      <div className="min-w-0 leading-tight">
                        <div className="flex items-center gap-1.5"><b className="text-gray-800 dark:text-gray-100">{l.name}</b><Tag color={APPT_STATUS[st].color} className="!m-0 !text-[10px]">{APPT_STATUS[st].label}</Tag></div>
                        <div className="mt-0.5 text-gray-500">{dm(a.at)} {hm(a.at)} · {l.mang}</div>
                      </div>
                      <AgentName id={a.bookedBy} sub="Telesales phụ trách" />
                    </div>
                  );
                })}
              </div>
            ) : <Empty text="Không có khách cần trả về" />}
          </Card>
        </div>
      </div>

      <Modal open={!!result} title={result ? `Kết quả tư vấn — ${m.leadById[result.leadId].name}` : ''} okText="Lưu kết quả" cancelText="Huỷ" onOk={saveResult} onCancel={() => setResult(null)} destroyOnClose>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="outcome" label="Kết quả">
            <Radio.Group optionType="button" buttonStyle="solid" options={[
              { value: 'won', label: 'Chốt' }, { value: 'deposit', label: 'Đặt cọc' }, { value: 'no_need', label: 'Chưa có nhu cầu' }, { value: 'medical', label: 'Rớt do bệnh lý' },
            ]} />
          </Form.Item>
          <Form.Item name="consultantId" label="Tư vấn viên" rules={[{ required: true, message: 'Chọn tư vấn viên' }]}>
            <Select options={branchCons.map(c => ({ value: c.id, label: <ConsultantName id={c.id} /> }))} />
          </Form.Item>
          {(outcome === 'won' || outcome === 'deposit') && (
            <Form.Item name="amount" label={outcome === 'won' ? 'Giá trị chốt (đ)' : 'Tiền cọc (đ)'} rules={[{ required: true, message: 'Nhập số tiền' }, { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' }]}>
              <InputNumber<number> className="!w-full" min={0} step={500000} formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v ?? '').replace(/\./g, ''))} />
            </Form.Item>
          )}
          {(outcome === 'no_need' || outcome === 'medical') && (
            <Form.Item name="reason" label={outcome === 'medical' ? 'Lý do / chỉ định bác sĩ' : 'Lý do khách chưa có nhu cầu'} rules={[{ required: true, message: 'Ghi lý do để Telesales chăm tiếp' }]}>
              <Input.TextArea rows={2} placeholder={outcome === 'medical' ? 'VD: Bác sĩ chỉ định điều trị viêm da trước' : 'VD: Cần bàn thêm với gia đình, giá cao hơn dự kiến'} />
            </Form.Item>
          )}
          <p className="text-xs text-gray-500">{outcome === 'no_need' || outcome === 'medical' ? 'Khách sẽ trả về Telesales phụ trách để chăm tiếp.' : 'Khách chốt / cọc sẽ chuyển sang CSKH.'}</p>
        </Form>
      </Modal>
    </div>
  );
}
